/**
 * Client-side skill/plugin file fetch service for the Package Files viewer.
 *
 * Fetches file bodies from OUR BFF (`/api/skills/files/body`), which proxies GitHub with an
 * authenticated GitHub App installation token + cache. This escapes the browser's 60-req/hr
 * unauthenticated per-IP GitHub rate limit. The BFF owns the GitHub source coordinates
 * (host/owner/repo/ref), resolved server-side from the listing itself — the client only sends
 * `listingId` and a folder-relative `file` path, never a repo URL or coordinates.
 */

import axios from 'axios';
import { APPX_SITE_API_URL } from '~/modules/config';
import { handleException } from '~/modules/utilities/error_utilities';
import { http } from '~/modules/utilities/http';

/**
 * Fetches a single skill or plugin file's body from the BFF.
 *
 * Returns the file's text content. Failures are logged and rethrown so the viewer can render an
 * explicit retry state instead of confusing a failed request with a legitimately empty file.
 *
 * The client sends `listingId` (the skill or plugin listing's `external_id`) and `file` (a path
 * relative to that listing's source folder, e.g. 'SKILL.md', 'assets/foo.png') — never a repo URL
 * or coordinates. The BFF looks the listing up itself and resolves the trusted source folder and
 * commit before fetching from GitHub.
 *
 * @param listingId The skill or plugin listing's external_id
 * @param file The folder-relative path of the file to fetch (e.g. 'SKILL.md')
 * @param signal Optional AbortSignal to cancel the request
 * @returns The file's text content
 * @throws On cancellation or a real BFF/upstream failure
 */
export async function fetchSkillFileBody(listingId: string, file: string, signal?: AbortSignal): Promise<string> {
    let data: { body?: string; error?: boolean } | null | undefined;
    try {
        ({ data } = await http.get(
            `${APPX_SITE_API_URL}/skills/files/body?listingId=${encodeURIComponent(listingId)}&file=${encodeURIComponent(file)}`,
            {
                signal,
                // This service owns the retry-state warning below. Suppress the shared HTTP layer's
                // default error log so a rejected request produces one client-side Splunk record.
                suppressErrorLogging: true,
            },
        ));
    } catch (error) {
        // If the request was aborted (user clicked another file), rethrow so the caller can detect
        // it and avoid caching '' or showing an error state. An abort is expected, not a failure.
        if (axios.isCancel(error)) {
            throw error;
        }
        // Degraded but functional (docs/logging-decision-guide.md, "warn"): the viewer handles the
        // rethrown error with an explicit retry state.
        handleException(error, 'skillGithubFetch.fetchSkillFileBody', {
            level: 'warn',
        });
        throw error;
    }
    if (data?.error) {
        // The BFF already reported the upstream failure with server request context. Preserve the
        // UI's retry behavior without emitting a second, uncorrelated client warning.
        throw new Error('The package file could not be loaded');
    }
    return data?.body ?? '';
}
